import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  RefreshCw,
  Search,
  ArrowRight,
  ArrowUpRight,
  Brain,
  Atom,
  Bitcoin,
  TrendingUp,
  ExternalLink,
  Loader2,
  X,
  ChevronRight,
  Filter,
  Sparkles,
  Activity,
} from 'lucide-react';
import { formatApiErrorDetail } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SECTION_META = {
  ai: {
    label: 'Artificial Intelligence',
    short: 'AI',
    subtitle: 'Models, Research, Agents & Infrastructure',
    color: '#39ff14',
    bg: 'rgba(57, 255, 20, 0.08)',
    border: 'rgba(57, 255, 20, 0.35)',
    icon: Brain,
    updateLabel: 'Every 2 hours',
  },
  quantum: {
    label: 'Quantum Computing',
    short: 'Quantum',
    subtitle: 'Hardware, Algorithms & Breakthroughs',
    color: '#a855f7',
    bg: 'rgba(168, 85, 247, 0.08)',
    border: 'rgba(168, 85, 247, 0.35)',
    icon: Atom,
    updateLabel: 'Every 2 hours',
  },
  digital_assets: {
    label: 'Digital Assets',
    short: 'Digital Assets',
    subtitle: 'Blockchain, Tokenization & Web3',
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.08)',
    border: 'rgba(245, 158, 11, 0.35)',
    icon: Bitcoin,
    updateLabel: 'Every 2 hours',
  },
};

const relativeTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diffSec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 7 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
  return d.toLocaleDateString();
};

const formatLastUpdated = (iso) => {
  if (!iso) return '—';
  const diffSec = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diffSec < 90) return 'Just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)} min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)} h ago`;
  return new Date(iso).toLocaleString();
};

const SourceFavicon = ({ url, sourceName, color }) => {
  let host = '';
  try { host = new URL(url).hostname; } catch { /* ignore */ }
  const initial = (sourceName || '?')[0].toUpperCase();
  const [errored, setErrored] = useState(false);
  if (host && !errored) {
    return (
      <img
        src={`https://www.google.com/s2/favicons?domain=${host}&sz=64`}
        alt={sourceName}
        onError={() => setErrored(true)}
        className="w-3.5 h-3.5 rounded shrink-0 opacity-90"
      />
    );
  }
  return (
    <span
      className="w-3.5 h-3.5 rounded shrink-0 flex items-center justify-center font-mono text-[8px] font-bold"
      style={{ background: `${color}22`, color: color }}
    >{initial}</span>
  );
};

const ArticleThumb = ({ url, title, sectionColor }) => {
  const [errored, setErrored] = useState(false);
  let host = '';
  try { host = new URL(url).hostname; } catch { /* ignore */ }
  if (host && !errored) {
    return (
      <div className="w-14 h-14 rounded border border-slate-800 bg-slate-900/60 shrink-0 flex items-center justify-center overflow-hidden">
        <img
          src={`https://www.google.com/s2/favicons?domain=${host}&sz=128`}
          alt={title}
          onError={() => setErrored(true)}
          className="w-7 h-7"
        />
      </div>
    );
  }
  return (
    <div
      className="w-14 h-14 rounded border shrink-0 flex items-center justify-center font-display font-black text-base"
      style={{ background: `${sectionColor}11`, color: sectionColor, borderColor: `${sectionColor}33` }}
    >{(title || '?')[0].toUpperCase()}</div>
  );
};

const TopStoryRow = ({ article, sectionColor }) => (
  <a
    href={article.url}
    target="_blank"
    rel="noopener noreferrer"
    className="flex gap-3 group rounded-md p-1.5 -mx-1.5 hover:bg-slate-900/40 transition-all"
    data-testid={`top-story-${article.id}`}
  >
    <ArticleThumb url={article.url} title={article.title} sectionColor={sectionColor} />
    <div className="flex-1 min-w-0 space-y-1">
      <h5 className="font-display font-semibold text-[13px] text-slate-100 leading-snug line-clamp-2 group-hover:text-white transition-colors">
        {article.title}
      </h5>
      <div className="flex items-center gap-1.5 text-[10px] font-mono text-slate-500">
        <SourceFavicon url={article.url} sourceName={article.source_name} color={sectionColor} />
        <span className="text-slate-400">{article.source_name}</span>
        <span>·</span>
        <span>{relativeTime(article.published_at)}</span>
        <span className="w-1.5 h-1.5 rounded-full ml-0.5" style={{ background: sectionColor }} />
      </div>
      {article.tags && article.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 pt-0.5">
          {article.tags.slice(0, 3).map((tag) => (
            <span key={tag} className="font-mono text-[8px] text-slate-300 bg-slate-900 border border-slate-800/70 px-1.5 py-0.5 rounded">
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  </a>
);

const SectionCard = ({ section, meta, stats, topStories, onViewAll, loading }) => {
  const Icon = meta.icon;
  return (
    <div
      className="glass-card rounded-2xl p-6 flex flex-col gap-5 transition-all relative overflow-hidden"
      style={{ border: `1px solid ${meta.border}` }}
      data-testid={`section-card-${section}`}
    >
      <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full pointer-events-none blur-3xl opacity-30" style={{ background: meta.color }} />
      <div className="flex items-start justify-between gap-3 relative">
        <div className="flex items-start gap-3">
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center border shrink-0"
            style={{ background: meta.bg, color: meta.color, borderColor: meta.border }}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div className="space-y-1 pt-1">
            <h3 className="font-display font-black text-base text-white uppercase tracking-tight leading-none">
              {meta.label}
            </h3>
            <p className="text-[11px] text-slate-400 leading-snug">{meta.subtitle}</p>
          </div>
        </div>
        <button
          onClick={onViewAll}
          className="w-9 h-9 rounded-full border flex items-center justify-center shrink-0 transition-all hover:scale-105 cursor-pointer"
          style={{ borderColor: meta.border, color: meta.color, background: meta.bg }}
          data-testid={`section-open-${section}`}
          aria-label={`Open ${meta.label}`}
        >
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 relative">
        <div className="bg-slate-950/50 border border-slate-800/60 rounded-lg py-3 text-center">
          <div className="font-display font-extrabold text-xl text-white">{stats?.new_today ?? '—'}</div>
          <div className="font-mono text-[9px] text-slate-500 uppercase tracking-widest mt-1">New Today</div>
        </div>
        <div className="bg-slate-950/50 border border-slate-800/60 rounded-lg py-3 text-center">
          <div className="font-display font-extrabold text-xl text-white">{stats?.this_week ?? '—'}</div>
          <div className="font-mono text-[9px] text-slate-500 uppercase tracking-widest mt-1">This Week</div>
        </div>
        <div className="bg-slate-950/50 border border-slate-800/60 rounded-lg py-3 text-center">
          <div className="font-display font-extrabold text-xl flex items-center justify-center gap-0.5" style={{ color: meta.color }}>
            <TrendingUp className="w-3.5 h-3.5" />
            <span>{stats?.trending_pct ?? 0}%</span>
          </div>
          <div className="font-mono text-[9px] text-slate-500 uppercase tracking-widest mt-1">Trending</div>
        </div>
      </div>

      <div className="space-y-2 relative">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
          <span className="font-mono text-[10px] uppercase tracking-widest font-bold" style={{ color: meta.color }}>
            Top Stories
          </span>
        </div>
        <div className="space-y-3 min-h-[200px]">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-500 text-xs font-mono">
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> Loading feed…
            </div>
          ) : topStories.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-800 bg-slate-950/40 p-5 text-center space-y-1.5" data-testid={`empty-${section}`}>
              <Activity className="w-4 h-4 mx-auto" style={{ color: meta.color }} />
              <p className="text-xs text-slate-400">No latest news available right now.</p>
              <p className="text-[10px] font-mono text-slate-600">New articles are fetched every 2 hours from {meta.label}.</p>
            </div>
          ) : (
            topStories.map((a) => <TopStoryRow key={a.id} article={a} sectionColor={meta.color} />)
          )}
        </div>
      </div>

      <button
        onClick={onViewAll}
        className="text-left font-mono text-[11px] font-semibold tracking-wide flex items-center gap-1.5 group cursor-pointer relative"
        style={{ color: meta.color }}
        data-testid={`view-all-${section}`}
      >
        <span>View all {meta.short} news</span>
        <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
      </button>
    </div>
  );
};

const FeedSourcesPanel = ({ sources }) => {
  const order = ['ai', 'quantum', 'digital_assets'];
  return (
    <section className="glass-card rounded-2xl border border-slate-800 p-6 md:p-8 space-y-6" data-testid="feed-sources-panel">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#00f0ff]" />
            <h4 className="font-mono text-[11px] uppercase tracking-widest font-extrabold text-slate-300">Feed Sources</h4>
          </div>
          <p className="text-[11px] text-slate-500 mt-1 font-light">Real-time RSS sources powering the intelligence dashboard.</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {order.map((sec) => {
          const meta = SECTION_META[sec];
          const Icon = meta.icon;
          const list = sources?.[sec] || [];
          return (
            <div key={sec} className="space-y-3 border-t md:border-t-0 md:border-l border-slate-900 md:pl-6 pt-4 md:pt-0 first:border-l-0 first:pl-0">
              <div className="flex items-center gap-2">
                <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
                <span className="font-mono text-[10px] uppercase tracking-widest font-bold" style={{ color: meta.color }}>
                  {meta.short} Sources <span className="text-slate-500">({list.length})</span>
                </span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {list.map((s) => (
                  <a
                    key={s.id}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-2.5 py-1 rounded border border-slate-800 bg-slate-900/60 hover:bg-slate-900 hover:border-slate-700 transition-all font-mono text-[10px] text-slate-300 hover:text-white flex items-center gap-1.5"
                    data-testid={`source-chip-${s.id}`}
                  >
                    <SourceFavicon url={s.url} sourceName={s.name} color={meta.color} />
                    <span>{s.name}</span>
                  </a>
                ))}
              </div>
              <div className="font-mono text-[9px] text-slate-600 pt-2 border-t border-slate-900/60">
                Update: <span style={{ color: meta.color }}>{meta.updateLabel}</span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const SectionDetailView = ({ section, onBack, subcategories }) => {
  const meta = SECTION_META[section];
  const Icon = meta.icon;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [subcat, setSubcat] = useState('');

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ section, limit: '60' });
      if (subcat) params.append('subcategory', subcat);
      if (q.trim()) params.append('q', q.trim());
      const res = await axios.get(`${API}/insights/articles?${params}`);
      setItems(res.data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [section, subcat, q]);

  useEffect(() => {
    const t = setTimeout(fetchList, q ? 350 : 0);
    return () => clearTimeout(t);
  }, [fetchList, q]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid={`detail-${section}`}
    >
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="space-y-2">
          <button
            onClick={onBack}
            className="font-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-white flex items-center gap-1 cursor-pointer"
            data-testid="btn-back-dashboard"
          >
            <ChevronRight className="w-3 h-3 rotate-180" /> Back to Intelligence Hub
          </button>
          <div className="flex items-center gap-3">
            <div
              className="w-11 h-11 rounded-lg flex items-center justify-center border"
              style={{ background: meta.bg, color: meta.color, borderColor: meta.border }}
            >
              <Icon className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-display font-black text-2xl text-white uppercase tracking-tight">{meta.label}</h2>
              <p className="text-xs text-slate-400">{meta.subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 sticky top-[72px] z-20 bg-slate-950/80 backdrop-blur-md p-3 -mx-3 rounded-lg border border-slate-900/60">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-slate-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${meta.short} articles, tags, sources...`}
            className="w-full pl-9 pr-3 py-2 bg-slate-950 border border-slate-800 rounded font-mono text-xs text-white focus:outline-none focus:border-[#00f0ff] focus:ring-1 focus:ring-[#00f0ff]/40 transition-all placeholder:text-slate-600"
            data-testid="detail-search"
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto md:overflow-visible">
          <Filter className="w-3.5 h-3.5 text-slate-500 shrink-0" />
          <button
            onClick={() => setSubcat('')}
            className={`px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider border shrink-0 transition-all cursor-pointer ${!subcat ? 'text-white' : 'border-slate-800 bg-slate-900/40 text-slate-400 hover:text-slate-200'}`}
            style={!subcat ? { background: meta.bg, borderColor: meta.border, color: meta.color } : {}}
            data-testid="filter-all"
          >
            All
          </button>
          {subcategories.map((sc) => (
            <button
              key={sc}
              onClick={() => setSubcat(sc)}
              className={`px-3 py-1.5 rounded font-mono text-[10px] uppercase tracking-wider border shrink-0 transition-all cursor-pointer whitespace-nowrap ${subcat === sc ? 'text-white' : 'border-slate-800 bg-slate-900/40 text-slate-400 hover:text-slate-200'}`}
              style={subcat === sc ? { background: meta.bg, borderColor: meta.border, color: meta.color } : {}}
              data-testid={`filter-${sc.toLowerCase().replace(/\W+/g, '-')}`}
            >
              {sc}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500 font-mono text-xs gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading articles…
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-800 bg-slate-950/40 p-12 text-center space-y-2" data-testid={`detail-empty-${section}`}>
          <Activity className="w-6 h-6 mx-auto" style={{ color: meta.color }} />
          <p className="text-sm text-slate-300">No latest news available right now.</p>
          <p className="text-[11px] font-mono text-slate-500">Try clearing filters, or check back after the next ingestion run.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((a) => (
            <a
              key={a.id}
              href={a.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group glass-card rounded-xl border border-slate-800 hover:border-slate-700 p-5 space-y-3 transition-all"
              style={{ '--hover-color': meta.color }}
              data-testid={`article-card-${a.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="font-mono text-[9px] uppercase tracking-widest px-2 py-0.5 rounded border" style={{ background: meta.bg, color: meta.color, borderColor: meta.border }}>
                  {a.subcategory}
                </span>
                <ExternalLink className="w-3.5 h-3.5 text-slate-500 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-display font-bold text-sm text-white leading-snug line-clamp-3 group-hover:text-white">
                {a.title}
              </h3>
              {a.summary && (
                <p className="text-[12px] text-slate-400 leading-relaxed font-light line-clamp-3">
                  {a.summary}
                </p>
              )}
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(a.tags || []).slice(0, 4).map((t) => (
                  <span key={t} className="font-mono text-[8px] text-slate-300 bg-slate-900 border border-slate-800/80 px-1.5 py-0.5 rounded">{t}</span>
                ))}
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-slate-900/60 text-[10px] font-mono text-slate-500">
                <div className="flex items-center gap-1.5">
                  <SourceFavicon url={a.url} sourceName={a.source_name} color={meta.color} />
                  <span className="text-slate-300">{a.source_name}</span>
                </div>
                <span>{relativeTime(a.published_at)}</span>
              </div>
            </a>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export const TechFeedsView = () => {
  const [view, setView] = useState({ mode: 'dashboard', section: null });
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState(null);
  const [sectionsMeta, setSectionsMeta] = useState(null);
  const [topStories, setTopStories] = useState({ ai: [], quantum: [], digital_assets: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [sectionsRes, statsRes, aiRes, qRes, daRes] = await Promise.all([
        axios.get(`${API}/insights/sections`),
        axios.get(`${API}/insights/stats`),
        axios.get(`${API}/insights/articles?section=ai&limit=3`),
        axios.get(`${API}/insights/articles?section=quantum&limit=3`),
        axios.get(`${API}/insights/articles?section=digital_assets&limit=3`),
      ]);
      setSources(sectionsRes.data.sources);
      setSectionsMeta(sectionsRes.data.sections);
      setStats(statsRes.data);
      setTopStories({ ai: aiRes.data.items, quantum: qRes.data.items, digital_assets: daRes.data.items });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message || 'Failed to load feeds.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleRefresh = async () => {
    setRefreshing(true); setError('');
    try {
      await axios.post(`${API}/insights/refresh`);
      await loadAll();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message || 'Refresh failed.');
    } finally {
      setRefreshing(false);
    }
  };

  const lastUpdated = useMemo(() => formatLastUpdated(stats?.last_run), [stats?.last_run]);

  if (view.mode === 'detail') {
    const subs = sectionsMeta?.[view.section]?.subcategories || SECTION_META[view.section]?.subcategories || [];
    return (
      <div className="space-y-6">
        <SectionDetailView
          section={view.section}
          subcategories={subs}
          onBack={() => setView({ mode: 'dashboard', section: null })}
        />
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-10" data-testid="tech-feeds-view">
      <section className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00f0ff]/10 border border-[#00f0ff]/20 rounded-full">
            <Activity className="w-3 h-3 text-[#00f0ff] shrink-0" />
            <span className="font-mono text-[10px] text-[#00f0ff] tracking-widest font-semibold uppercase">03 // INTELLIGENCE HUB</span>
          </div>
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-white tracking-tight uppercase">
            INDUSTRY INTELLIGENCE <span className="text-[#00f0ff]">HUB</span>
          </h2>
          <p className="text-slate-400 text-xs sm:text-sm font-light leading-relaxed max-w-2xl">
            Real-time curated insights from the frontiers of AI, Quantum Computing, and Digital Assets.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="px-3 py-2 rounded border border-slate-800 bg-slate-950/60 flex items-center gap-2" data-testid="last-updated">
            <span className="w-1.5 h-1.5 rounded-full bg-[#39ff14] signal-dot" />
            <span className="font-mono text-[10px] text-slate-400">Last Updated:</span>
            <span className="font-mono text-[10px] text-slate-200">{lastUpdated}</span>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 rounded border border-[#00f0ff]/40 bg-[#00f0ff]/10 text-[#00f0ff] hover:bg-[#00f0ff]/20 disabled:opacity-50 font-mono text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 transition-all cursor-pointer"
            data-testid="btn-refresh-all"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Refreshing…' : 'Refresh All'}</span>
          </button>
        </div>
      </section>

      {error && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-rose-300 text-xs font-mono" data-testid="feeds-error">
          ! {error}
        </div>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {['ai', 'quantum', 'digital_assets'].map((sec) => (
          <SectionCard
            key={sec}
            section={sec}
            meta={SECTION_META[sec]}
            stats={stats?.[sec]}
            topStories={topStories[sec] || []}
            onViewAll={() => setView({ mode: 'detail', section: sec })}
            loading={loading}
          />
        ))}
      </section>

      <FeedSourcesPanel sources={sources} />
    </motion.div>
  );
};
